import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { gallery } from "../data/content";
import { ResponsiveImage } from "./ResponsiveImage";
import { GalleryMetadata } from "./GalleryMetadata";

type CarouselApi = UseEmblaCarouselType[1];

// Interaction architecture adapted from the official shadcn/ui Carousel.
export function HomeGalleryCarousel() {
  const [viewportRef, api] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
    skipSnaps: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(1);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const syncState = useCallback((carousel: CarouselApi) => {
    if (!carousel) return;
    setSelectedIndex(carousel.selectedScrollSnap());
    setSnapCount(carousel.scrollSnapList().length);
    setCanScrollPrev(carousel.canScrollPrev());
    setCanScrollNext(carousel.canScrollNext());
  }, []);

  useEffect(() => {
    if (!api) return;
    const frame = window.requestAnimationFrame(() => syncState(api));
    api.on("select", syncState);
    api.on("reInit", syncState);
    return () => {
      window.cancelAnimationFrame(frame);
      api.off("select", syncState);
      api.off("reInit", syncState);
    };
  }, [api, syncState]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      api?.scrollPrev();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      api?.scrollNext();
    }
  };

  return (
    <section
      className="home-gallery__carousel"
      aria-roledescription="carrossel"
      aria-label="Prévia da galeria de Palmas"
      onKeyDownCapture={handleKeyDown}
    >
      <div className="home-gallery__viewport" ref={viewportRef}>
        <div className="home-gallery__track">
          {gallery.map((item, index) => (
            <article
              className="home-gallery__slide"
              key={item.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} de ${gallery.length}: ${item.title}`}
            >
              <Link className="home-gallery__item" to="/galeria">
                <ResponsiveImage
                  media={item.media}
                  alt={item.media.alt}
                  sizes="(max-width: 700px) 88vw, (max-width: 1100px) 58vw, 43vw"
                />
                <span className="home-gallery__caption">
                  <span>
                    <small>{item.category}</small>
                    <strong>{item.media.caption || item.title}</strong>
                    <GalleryMetadata item={item} />
                  </span>
                  <ArrowUpRight aria-hidden="true" size={21} />
                </span>
              </Link>
            </article>
          ))}
        </div>
      </div>

      <div className="home-gallery__controls">
        <p aria-live="polite">
          <strong>{String(selectedIndex + 1).padStart(2, "0")}</strong>
          <span aria-hidden="true">/</span>
          <span>{String(snapCount).padStart(2, "0")}</span>
        </p>
        <div className="home-gallery__progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${(selectedIndex + 1) / snapCount})` }} />
        </div>
        <div className="home-gallery__arrows">
          <button
            type="button"
            onClick={() => api?.scrollPrev()}
            disabled={!canScrollPrev}
            aria-label="Registro anterior"
          >
            <ArrowLeft aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => api?.scrollNext()}
            disabled={!canScrollNext}
            aria-label="Próximo registro"
          >
            <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
